import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../hooks/useAuth';
import supabaseService from '../services/supabaseService';
import cloudinaryService from '../services/cloudinaryService';
import { 
  FiArrowRight, 
  FiAlertCircle, 
  FiCheckCircle, 
  FiClock, 
  FiUploadCloud, 
  FiX, 
  FiAlertTriangle,
  FiBookOpen,
  FiCreditCard
} from 'react-icons/fi';
import { toast } from 'react-hot-toast';

export default function ProviderBankAccountScreen() {
  const { user } = useAuth();
  const navigate = useNavigate();

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [bankAccount, setBankAccount] = useState(null);

  // Form states
  const [rib, setRib] = useState('');
  const [chequeFile, setChequeFile] = useState(null);
  const [chequePreview, setChequePreview] = useState('');

  useEffect(() => {
    loadBankAccount();
  }, [user]);

  const loadBankAccount = async () => {
    if (!user) return;
    setLoading(true);
    try {
      const data = await supabaseService.getProviderBankAccount(user.id);
      if (data) {
        setBankAccount(data);
        setRib(data.rib || '');
      }
    } catch (e) {
      console.error(e);
      toast.error('فشل تحميل معلومات الحساب البنكي');
    } finally {
      setLoading(false);
    }
  };

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      if (!file.type.startsWith('image/')) {
        toast.error('يرجى اختيار صورة صالحة للشيك');
        return;
      }
      setChequeFile(file);
      setChequePreview(URL.createObjectURL(file));
    }
  };

  const removeSelectedFile = () => {
    setChequeFile(null);
    setChequePreview('');
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    // Validate RIB: usually 20 digits
    const cleanedRib = rib.replace(/\s+/g, '');
    if (!/^\d{20}$/.test(cleanedRib)) {
      toast.error('رقم الـ RIB يجب أن يتكون من 20 رقماً بالضبط');
      return;
    }

    if (!chequeFile && (!bankAccount || !bankAccount.cheque_url)) {
      toast.error('يرجى رفع صورة الشيك المشطوب');
      return;
    }

    setSaving(true);
    const toastId = toast.loading('جاري رفع المستندات وحفظ البيانات...');

    try {
      let chequeUrl = bankAccount?.cheque_url || '';

      if (chequeFile) {
        // Upload via Cloudinary service (which maps to Backblaze B2/ccp_photo)
        const uploadedUrl = await cloudinaryService.uploadImage(chequeFile, 'document');
        if (!uploadedUrl) {
          throw new Error('فشل رفع صورة الشيك المشطوب');
        }
        chequeUrl = uploadedUrl;
      }

      await supabaseService.saveProviderBankAccount(user.id, cleanedRib, chequeUrl);
      toast.success('تم إرسال طلب ربط الحساب للمراجعة بنجاح', { id: toastId });
      loadBankAccount();
      removeSelectedFile();
    } catch (err) {
      console.error(err);
      toast.error(err.message || 'حدث خطأ أثناء الحفظ، يرجى المحاولة لاحقاً', { id: toastId });
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-gray-900"></div>
      </div>
    );
  }

  // Determine UI State based on status
  // status can be: pending, accepted, rejected, or null (no account yet)
  const status = bankAccount?.status;

  return (
    <div className="min-h-screen bg-gray-50 font-sans flex flex-col" dir="rtl">
      {/* Header */}
      <div className="bg-white border-b border-gray-200 sticky top-0 z-10 px-4 py-3.5 flex items-center gap-3 shadow-sm shrink-0">
        <button onClick={() => navigate(-1)} className="p-2 rounded-full bg-gray-100 hover:bg-gray-200 transition-colors">
          <FiArrowRight className="text-gray-700" size={20} />
        </button>
        <h1 className="text-lg font-bold text-gray-900">ربط الحساب البنكي / البريدي</h1>
      </div>

      {/* Main Content */}
      <div className="flex-1 overflow-y-auto p-4 md:p-6 flex flex-col items-center">
        <div className="w-full max-w-lg space-y-6">
          
          {/* 1. Status View: Pending Review */}
          {status === 'pending' && (
            <div className="bg-white border border-gray-200 p-6 md:p-8 rounded-2xl shadow-sm space-y-5 text-center flex flex-col items-center">
              <div className="w-16 h-16 rounded-full bg-amber-50 flex items-center justify-center animate-pulse">
                <FiClock className="text-amber-500" size={36} />
              </div>
              <div className="space-y-2">
                <h2 className="text-xl font-bold text-gray-900">الحساب البنكي قيد المراجعة</h2>
                <p className="text-sm text-gray-500 leading-relaxed">
                  لقد استلمنا بيانات حسابك البنكي وهي حالياً قيد المراجعة والتأكيد من قبل الإدارة. سيتم تفعيل حسابك بمجرد قبول الطلب.
                </p>
              </div>

              <div className="w-full bg-gray-50 p-4 rounded-xl border border-gray-150 text-right space-y-3">
                <div>
                  <span className="text-xs text-gray-400 block">رقم الـ RIB المسجل</span>
                  <span className="font-mono text-base font-bold text-gray-800 tracking-wider">
                    {bankAccount.rib.replace(/(.{4})/g, '$1 ').trim()}
                  </span>
                </div>
                {bankAccount.cheque_url && (
                  <div>
                    <span className="text-xs text-gray-400 block mb-1.5">صورة الشيك المشطوب</span>
                    <a 
                      href={bankAccount.cheque_url} 
                      target="_blank" 
                      rel="noopener noreferrer" 
                      className="inline-flex items-center gap-1.5 text-xs text-blue-600 font-bold hover:underline"
                    >
                      <FiUploadCloud size={14} /> عرض الصورة المرفوعة
                    </a>
                  </div>
                )}
              </div>

              <div className="bg-amber-50/60 border border-amber-100 p-3.5 rounded-lg flex items-start gap-2.5 text-xs text-amber-800 text-right w-full">
                <FiAlertCircle className="text-amber-500 mt-0.5 shrink-0" size={16} />
                <span>لا يمكنك تعديل معلومات الحساب أو إعادة رفع مستندات جديدة أثناء فترة المراجعة والتدقيق.</span>
              </div>
            </div>
          )}

          {/* 2. Status View: Accepted / Approved */}
          {status === 'accepted' && (
            <div className="bg-white border border-gray-200 p-6 md:p-8 rounded-2xl shadow-sm space-y-5 text-center flex flex-col items-center">
              <div className="w-16 h-16 rounded-full bg-emerald-50 flex items-center justify-center">
                <FiCheckCircle className="text-emerald-500" size={38} />
              </div>
              <div className="space-y-2">
                <h2 className="text-xl font-bold text-gray-900">تم ربط وتفعيل الحساب بنجاح</h2>
                <p className="text-sm text-gray-500 leading-relaxed">
                  تهانينا، تم تأكيد وقبول حسابك البنكي من قبل الإدارة. يمكنك الآن استقبال أرباحك وعائداتك على هذا الحساب مباشرة.
                </p>
              </div>

              <div className="w-full bg-gray-900 text-white p-4 rounded-xl text-right space-y-3 relative overflow-hidden">
                <div className="absolute top-0 left-0 w-24 h-24 bg-white/5 rounded-full transform -translate-x-6 -translate-y-6"></div>
                <div className="relative z-10">
                  <span className="text-[10px] text-gray-400 uppercase tracking-widest block mb-0.5">الرقم المالي المعتمد</span>
                  <span className="font-mono text-lg font-bold tracking-wider">
                    {bankAccount.rib.replace(/(.{4})/g, '$1 ').trim()}
                  </span>
                </div>
                <div className="pt-2 border-t border-white/10 flex justify-between items-center relative z-10">
                  <span className="text-[11px] bg-emerald-500/20 text-emerald-400 px-2 py-0.5 rounded-full font-bold">نشط ومفعل</span>
                  <FiCreditCard className="text-white/20" size={20} />
                </div>
              </div>

              <div className="bg-blue-50/60 border border-blue-100 p-3.5 rounded-lg flex items-start gap-2.5 text-xs text-blue-800 text-right w-full">
                <FiAlertCircle className="text-blue-500 mt-0.5 shrink-0" size={16} />
                <span>لدواعي الأمان ومنع الاحتيال، لا يمكن تعديل أو تغيير الحساب البنكي بمجرد تفعيله. إذا كنت بحاجة لتحديث الحساب، يرجى التواصل مع الدعم الفني.</span>
              </div>
            </div>
          )}

          {/* 3. Form View: No Link OR Rejected Status */}
          {(status === null || status === undefined || status === 'rejected') && (
            <div className="space-y-6">
              {/* Alert if previously rejected */}
              {status === 'rejected' && (
                <div className="bg-red-50 border border-red-200 p-4.5 rounded-2xl flex items-start gap-3 text-red-800 shadow-sm animate-shake">
                  <FiAlertTriangle className="text-red-500 mt-0.5 shrink-0" size={20} />
                  <div className="text-right">
                    <p className="font-bold text-sm">تم رفض طلب الربط السابق</p>
                    <p className="text-xs text-red-700/90 mt-1 leading-relaxed">
                      يرجى مراجعة رقم الـ RIB وإعادة التقاط صورة واضحة للشيك المشطوب ثم إعادة الإرسال للمراجعة.
                    </p>
                  </div>
                </div>
              )}

              <div className="bg-white border border-gray-200 rounded-2xl p-6 md:p-7 shadow-sm space-y-6">
                <div className="text-right">
                  <h2 className="text-lg font-bold text-gray-900">إدخال معلومات الحساب البنكي</h2>
                  <p className="text-xs text-gray-500 mt-1">تأكد من إدخال بياناتك بشكل صحيح لضمان سرعة معالجة الطلبات المالية.</p>
                </div>

                <form onSubmit={handleSubmit} className="space-y-5">
                  {/* RIB Input */}
                  <div className="space-y-2 text-right">
                    <label className="block text-sm font-bold text-gray-700">رقم الحساب البريدي أو البنكي (RIB)</label>
                    <div className="relative">
                      <input 
                        type="text" 
                        maxLength="20"
                        value={rib}
                        onChange={(e) => setRib(e.target.value.replace(/\D/g, ''))}
                        placeholder="مثال: 00799999000000000000"
                        className="w-full p-3.5 border border-gray-300 rounded-xl font-mono text-lg tracking-widest text-left focus:border-gray-900 focus:ring-1 focus:ring-gray-900 outline-none transition"
                        required
                        disabled={saving}
                      />
                    </div>
                    <p className="text-[11px] text-gray-400">يجب أن يتكون رقم الـ RIB من 20 رقماً (بدون مسافات).</p>
                  </div>

                  {/* Cheque Upload */}
                  <div className="space-y-2 text-right">
                    <label className="block text-sm font-bold text-gray-700">صورة الشيك المشطوب (Chèque barré)</label>
                    
                    {!chequePreview ? (
                      <label className="border-2 border-dashed border-gray-300 hover:border-gray-900 rounded-2xl p-6 text-center cursor-pointer transition bg-gray-50 hover:bg-gray-100 flex flex-col items-center justify-center gap-2 min-h-36">
                        <input 
                          type="file" 
                          accept="image/*" 
                          className="hidden" 
                          onChange={handleFileChange}
                          disabled={saving}
                        />
                        <FiUploadCloud className="text-gray-400 w-10 h-10" />
                        <span className="text-sm font-bold text-gray-700">اضغط لرفع صورة الشيك</span>
                        <span className="text-xs text-gray-400">صيغ الصور المدعومة: PNG, JPG, JPEG</span>
                      </label>
                    ) : (
                      <div className="relative rounded-2xl overflow-hidden border border-gray-200 bg-gray-50 p-2.5">
                        <img 
                          src={chequePreview} 
                          alt="Cheque Preview" 
                          className="max-h-48 w-full object-contain rounded-xl"
                        />
                        <button 
                          type="button"
                          onClick={removeSelectedFile}
                          className="absolute top-4 left-4 p-2 bg-red-600 text-white rounded-full hover:bg-red-700 transition shadow-md"
                          disabled={saving}
                        >
                          <FiX size={16} />
                        </button>
                      </div>
                    )}

                    {bankAccount?.cheque_url && !chequePreview && (
                      <div className="flex items-center justify-between bg-gray-50 px-3 py-2 rounded-lg border border-gray-200">
                        <span className="text-xs text-gray-500 font-medium">الشيك المرفوع سابقاً متوفر</span>
                        <a 
                          href={bankAccount.cheque_url} 
                          target="_blank" 
                          rel="noopener noreferrer" 
                          className="text-xs text-blue-600 font-bold hover:underline"
                        >
                          عرض الشيك
                        </a>
                      </div>
                    )}
                  </div>

                  {/* Submit Button */}
                  <button
                    type="submit"
                    disabled={saving || !rib || (!chequeFile && !bankAccount?.cheque_url)}
                    className="w-full bg-gray-900 text-white py-3.5 rounded-xl font-bold hover:bg-gray-800 transition disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2 shadow-md hover:shadow-lg mt-2"
                  >
                    {saving ? (
                      <>
                        <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white"></div>
                        <span>جاري حفظ البيانات...</span>
                      </>
                    ) : (
                      <span>حفظ وإرسال للمراجعة</span>
                    )}
                  </button>
                </form>
              </div>

              {/* تعليمات هامة */}
              <div className="bg-white rounded-xl p-5 border border-gray-200 space-y-3">
                <h4 className="font-bold text-gray-900 text-sm text-right flex items-center gap-2 justify-end">
                  <span>ملاحظات هامة للقبول</span>
                  <FiBookOpen className="text-gray-500" size={16} />
                </h4>
                <ul className="space-y-2 text-xs text-gray-600 text-right list-disc list-inside">
                  <li>تأكد من أن الاسم الموجود على الشيك المشطوب يطابق الاسم المسجل في حسابك الشخصي.</li>
                  <li>يجب أن تكون أرقام الـ RIB واضحة تماماً ومقروءة في الصورة المرفوعة.</li>
                  <li>تستغرق عملية التحقق من الحساب عادة من 12 إلى 24 ساعة عمل.</li>
                </ul>
              </div>
            </div>
          )}

        </div>
      </div>
    </div>
  );
}
